// ---------------------------------------------------------------------------
// textTools.js — Text upgrades (spec group E).
//
// Registers a "Caseta text" (fabric.Textbox) inserter + a rich "Stil text"
// panel section into the DesignEditor foundation seam. All controls operate on
// whatever text object is selected (i-text / textbox / text), so they also
// style legacy designs that were saved as 'i-text'. The curated Google Fonts
// picker stays OWNED by the foundation (its built-in "Text" section) — we do
// NOT reimplement font selection here.
//
// Seam calls used (see the editor-API header in DesignEditor.js):
//   editor.addToolButton({ id, title, icon, onClick })   — Textbox inserter
//   editor.addPanelSection({ id, title, when, render, order })  — style panel
//
// No custom serialization props are needed: textAlign, underline, linethrough,
// overline, charSpacing, lineHeight, stroke, strokeWidth, paintFirst and shadow
// are all part of fabric's default (i)text/object serialization, so they
// survive undo + save with no extra registration.
//
// NOTE (seam): new text created by the FOUNDATION's "Text" button / `T` key is
// still a fabric.IText. The spec wants new text to be a Textbox for width-bound
// wrapping. We provide that via the "Caseta text" tool here; the foundation's
// addText() should also switch IText -> Textbox for full parity (flagged in the
// orchestrator report). Old 'i-text' JSON keeps deserializing either way.
// ---------------------------------------------------------------------------
import React, { Component } from 'react';
import { fabric } from 'fabric';

const TEXT_TYPES = ['i-text', 'textbox', 'text'];
const isTextObject = (o) => !!o && TEXT_TYPES.indexOf(o.type) !== -1;

function centerOf(editor) {
  const { width, height } = editor.getArtboard();
  return { x: width / 2, y: height / 2 };
}

// Insert a width-bound Textbox (wraps text onto multiple lines, unlike the
// foundation's single-run IText).
function addTextbox(editor) {
  const { width, height } = editor.getArtboard();
  const box = new fabric.Textbox('Scrie aici', {
    left: width / 2,
    top: height / 2,
    originX: 'center',
    originY: 'center',
    width: Math.max(80, Math.min(240, Math.round(width * 0.8))),
    fontFamily: 'Arial',
    fontSize: 32,
    fill: '#111111',
    textAlign: 'left',
    name: 'Caseta text',
  });
  editor.canvas.add(box);
  editor.canvas.setActiveObject(box);
  editor.requestRender();
  editor.pushHistory('Caseta text');
  editor.refreshLayers();
}

// Small inline glyph for a text-align option (4 horizontal bars).
function alignIcon(a) {
  const ys = [6, 10, 14, 18];
  const ws = [15, 9, 13, 7];
  return (
    <svg viewBox="0 0 24 24" width="16" height="16">
      {ys.map((y, i) => {
        const w = a === 'justify' ? 18 : ws[i];
        let x = 3;
        if (a === 'right') x = 21 - w;
        else if (a === 'center') x = (24 - w) / 2;
        return <line key={i} x1={x} y1={y} x2={x + w} y2={y} stroke="currentColor" strokeWidth="2" />;
      })}
    </svg>
  );
}

const ALIGNS = [
  { key: 'left', label: 'Aliniaza la stanga' },
  { key: 'center', label: 'Centreaza' },
  { key: 'right', label: 'Aliniaza la dreapta' },
  { key: 'justify', label: 'Aliniaza pe toata latimea' },
];

// The panel is a stateful component so its inputs re-read the live object after
// each edit (forceUpdate). Values always come from the fabric object, never a
// duplicated local copy, so selection changes are reflected automatically.
class TextStylePanel extends Component {
  get obj() {
    const o = this.props.editor.getActive();
    return isTextObject(o) ? o : null;
  }

  // fn mutates the object; history 'now' snapshots immediately (discrete
  // actions), otherwise one debounced snapshot per gesture (sliders).
  _mut = (fn, history, label) => {
    const o = this.obj;
    if (!o) return;
    fn(o);
    if (o.initDimensions) o.initDimensions();
    o.setCoords();
    this.props.editor.requestRender();
    if (history === 'now') this.props.editor.pushHistory(label);
    else this.props.editor.pushHistoryDebounced(label);
    this.forceUpdate();
  };

  setAlign = (a) => this._mut((o) => o.set('textAlign', a), 'now', 'Aliniere text');
  toggleProp = (prop, label) => this._mut((o) => o.set(prop, !o[prop]), 'now', label);
  setCharSpacing = (e) => { const v = parseInt(e.target.value, 10); this._mut((o) => o.set('charSpacing', v), 'debounce', 'Spatiere litere'); };
  setLineHeight = (e) => { const v = parseFloat(e.target.value); this._mut((o) => o.set('lineHeight', v), 'debounce', 'Inaltime rand'); };
  toUpper = () => this._mut((o) => o.set('text', String(o.text || '').toUpperCase()), 'now', 'Majuscule');

  setOutlineWidth = (e) => {
    const w = parseInt(e.target.value, 10);
    this._mut((o) => {
      const stroke = (typeof o.stroke === 'string' && o.stroke) ? o.stroke : '#000000';
      o.set({ strokeWidth: w, stroke, paintFirst: 'stroke', strokeLineJoin: 'round' });
    }, 'debounce', 'Contur text');
  };
  setOutlineColor = (e) => {
    const c = e.target.value;
    this._mut((o) => o.set({ stroke: c, paintFirst: 'stroke' }), 'debounce', 'Culoare contur text');
  };

  // One-click effect presets.
  presetHollow = () => this._mut((o) => {
    const base = (typeof o.fill === 'string' && o.fill && o.fill !== 'rgba(0,0,0,0)') ? o.fill : '#111111';
    o.set({ fill: 'rgba(0,0,0,0)', stroke: base, strokeWidth: 2, paintFirst: 'stroke', strokeLineJoin: 'round', shadow: null });
  }, 'now', 'Efect contur');
  presetLift = () => this._mut((o) => {
    o.set('shadow', new fabric.Shadow({ color: 'rgba(0,0,0,0.35)', blur: 8, offsetX: 0, offsetY: 6 }));
  }, 'now', 'Efect ridicat');
  presetShadow = () => this._mut((o) => {
    o.set('shadow', new fabric.Shadow({ color: 'rgba(0,0,0,0.55)', blur: 3, offsetX: 3, offsetY: 3 }));
  }, 'now', 'Efect umbra');

  render() {
    const o = this.obj;
    if (!o) return null;
    const align = o.textAlign || 'left';
    const charSpacing = o.charSpacing || 0;
    const lineHeight = o.lineHeight != null ? o.lineHeight : 1.16;
    const outlineWidth = o.strokeWidth || 0;
    const outlineColor = (typeof o.stroke === 'string' && o.stroke) ? o.stroke : '#000000';

    return (
      <>
        <div className="de-row">
          {ALIGNS.map((a) => (
            <button
              key={a.key}
              className={`de-btn-sm ${align === a.key ? 'active' : ''}`}
              onClick={() => this.setAlign(a.key)}
              title={a.label}
            >
              {alignIcon(a.key)}
            </button>
          ))}
        </div>

        <div className="de-row">
          <button className={`de-btn-sm ${o.underline ? 'active' : ''}`} onClick={() => this.toggleProp('underline', 'Subliniere')} title="Subliniat">
            <span style={{ textDecoration: 'underline' }}>U</span>
          </button>
          <button className={`de-btn-sm ${o.linethrough ? 'active' : ''}`} onClick={() => this.toggleProp('linethrough', 'Text taiat')} title="Taiat">
            <span style={{ textDecoration: 'line-through' }}>S</span>
          </button>
          <button className={`de-btn-sm ${o.overline ? 'active' : ''}`} onClick={() => this.toggleProp('overline', 'Supraliniere')} title="Supraliniat">
            <span style={{ textDecoration: 'overline' }}>O</span>
          </button>
          <button className="de-btn-sm" onClick={this.toUpper} title="Transforma in majuscule" style={{ width: 'auto', padding: '0 8px' }}>
            MAJ
          </button>
        </div>

        <div className="de-row">
          <label className="de-label">Spatiere</label>
          <input type="range" min="-50" max="800" step="10" value={charSpacing} onChange={this.setCharSpacing} className="de-range" />
          <span className="de-val">{charSpacing}</span>
        </div>
        <div className="de-row">
          <label className="de-label">Rand</label>
          <input type="range" min="0.7" max="3" step="0.05" value={lineHeight} onChange={this.setLineHeight} className="de-range" />
          <span className="de-val">{Number(lineHeight).toFixed(2)}</span>
        </div>

        <div className="de-row">
          <label className="de-label">Contur</label>
          <input type="color" className="de-color" value={outlineColor} onChange={this.setOutlineColor} title="Culoare contur" />
          <input type="range" min="0" max="8" step="1" value={outlineWidth} onChange={this.setOutlineWidth} className="de-range" />
          <span className="de-val">{outlineWidth}</span>
        </div>

        <div className="de-row">
          <button className="de-btn-sm" onClick={this.presetHollow} title="Text gol (doar contur)" style={{ width: 'auto', padding: '0 8px' }}>Contur</button>
          <button className="de-btn-sm" onClick={this.presetLift} title="Umbra fina sub text" style={{ width: 'auto', padding: '0 8px' }}>Ridicat</button>
          <button className="de-btn-sm" onClick={this.presetShadow} title="Umbra clasica" style={{ width: 'auto', padding: '0 8px' }}>Umbra</button>
        </div>
      </>
    );
  }
}

export function registerTextTools(editor) {
  editor.addToolButton({
    id: 'de-textbox',
    title: 'Caseta text (se incadreaza automat)',
    icon: (
      <svg viewBox="0 0 24 24" width="18" height="18">
        <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <path d="M8 9h8M8 12h8M8 15h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
    onClick: (ed) => addTextbox(ed),
  });

  editor.addPanelSection({
    id: 'de-text-style',
    title: 'Stil text',
    order: 10,
    when: (o) => isTextObject(o),
    render: (ed) => <TextStylePanel editor={ed} />,
  });
}

export default registerTextTools;
